/**
 * Utility functions for converting images to base64 data URLs for PDF export
 */

/**
 * Convert an image URL to base64 using the CORS proxy
 */
export async function convertImageToBase64(imageUrl: string): Promise<string | null> {
  if (!imageUrl || imageUrl.startsWith('data:')) {
    return imageUrl || null;
  }

  const proxyUrl = `/api/cors-image?url=${encodeURIComponent(imageUrl)}`;

  try {
    const response = await fetch(proxyUrl);
    if (!response.ok) {
      console.warn('Proxy returned error:', response.status);
      return null;
    }

    const blob = await response.blob();
    return await blobToDataUrl(blob);
  } catch (error) {
    console.warn('Failed to fetch image via proxy:', error);
    return null;
  }
}

/**
 * Convert blob to data URL
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string' && reader.result.startsWith('data:')) {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to data URL'));
      }
    };
    reader.onerror = () => reject(new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Load image and convert to data URL - tries CORS proxy first, then fallback methods
 */
export async function imageUrlToDataUrl(url: string): Promise<string | null> {
  if (url.startsWith('data:')) {
    return url;
  }

  // Try CORS proxy first (most reliable)
  try {
    const result = await convertImageToBase64(url);
    if (result) return result;
  } catch {
    // Continue to fallback
  }

  // Fallback: try direct fetch
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'include',
    });

    if (response.ok) {
      const blob = await response.blob();
      return await blobToDataUrl(blob);
    }
  } catch {
    // Return null if all methods fail
  }

  return null;
}

